import { Tooltip } from '@progress/kendo-react-tooltip'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMount } from 'react-use'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { VStack } from '~/components/elements/VStack'
import { DataDisplayTypeAtomSwitcher } from '~/components/features/DataDisplayTypeAtomSwitcher'
import {
  SegmentDisplaySwitcher,
  type SegmentDisplaySwitcherProps,
} from '~/components/features/SegmentDisplaySwitcher'
import { SharedBrandGroupSelector } from '~/components/features/SharedBrandGroupSelector'
import { useCurrentPrimarySurvey } from '~/hooks/useCurrentPrimarySurvey'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists, waitImmediate } from '~/utils'

import {
  dataDisplayTypeAtom,
  importanceQuestionIdAtom,
  isTabulationReadyAtom,
  liftSaQuestionIdAtom,
  npiDriverQuestionIdAtom,
  pendingScrollHashAtom,
  segmentDisplayTypeAtom,
} from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useCommonAnswerQuestions } from '../hooks/useCommonAnswerQuestions'
import { npiDriverDataAtom } from '../hooks/useNpiDriverTableData'
import { useTabulationQuery } from '../hooks/useTabulationQuery'
import { useTriggerTabulation } from '../hooks/useTriggerTabulation'
import { MANUAL_URLS, pathKey } from '../utils'

import { AnalyzeButton } from './AnalyzeButton'
import { BrandSelectorContainer } from './BrandSelectorContainer'
import { ManualLink } from './ManualLink'
import { Outputs } from './Outputs'
import { QuestionSelectionBox } from './QuestionSelectionBox'
import styles from './WhoAndWhatAnalysis.css'

const LabelWithTooltip = ({
  href,
  label,
  tooltip,
}: {
  href: string
  label: string
  tooltip: string
}) => (
  <HStack align="center" spacing={1}>
    <span>{label}</span>
    <Tooltip
      anchorElement="target"
      openDelay={500}
      showCallout={false}
      tooltipStyle={{ fontSize: 11 }}
    >
      <ManualLink className={styles.helpLink} href={href}>
        <Icon icon="help" size="1rem" title={tooltip} />
      </ManualLink>
    </Tooltip>
  </HStack>
)

export const WhoAndWhatAnalysis = () => {
  const survey = useCurrentPrimarySurvey()
  const surveyId = survey?.id ?? ''
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('basicAnalysis'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const isExecuting = useTabulationQuery()

  const restoreParams = useRestoreParams()
  const triggerTabulation = useTriggerTabulation()
  const navigate = useNavigate()
  const location = useLocation()
  const [shouldTriggerTabulation, setShouldTriggerTabulation] =
    React.useState(false)

  useMount(() => {
    const restored = restoreParams(location.state)
    if (restored !== null) {
      setShouldTriggerTabulation(true)
      navigate(location.pathname, { replace: true })
    }
  })

  React.useEffect(() => {
    if (!shouldTriggerTabulation) {
      return
    }
    setShouldTriggerTabulation(false)
    void triggerTabulation()
  }, [shouldTriggerTabulation, triggerTabulation])

  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const pendingScrollHash = useAtomValue(pendingScrollHashAtom)
  const setPendingScrollHash = useSetAtom(pendingScrollHashAtom)
  const npiDriverData = useAtomValue(npiDriverDataAtom)

  React.useEffect(() => {
    if (!pendingScrollHash || !isTabulationReady) {
      return
    }

    let cancelled = false
    waitImmediate().then(() => {
      if (cancelled) {
        return
      }
      const element = document.getElementById(pendingScrollHash)
      if (!exists(element)) {
        setPendingScrollHash(null)

        return
      }

      element.scrollIntoView({ behavior: 'smooth' })
      setPendingScrollHash(null)
    })

    return () => {
      cancelled = true
    }
  }, [
    isTabulationReady,
    npiDriverData,
    pendingScrollHash,
    setPendingScrollHash,
  ])

  const { importanceQuestions, liftSaQuestions, npiDriverQuestions } =
    useCommonAnswerQuestions()

  const [npiDriverQuestionId, setNpiDriverQuestionId] = useAtom(
    npiDriverQuestionIdAtom,
  )
  const [importanceQuestionId, setImportanceQuestionId] = useAtom(
    importanceQuestionIdAtom,
  )
  const [liftSaQuestionId, setLiftSaQuestionId] = useAtom(liftSaQuestionIdAtom)

  const logEvent = useLogEvent()
  const handleSegmentDisplayTypeChange = React.useCallback<
    EH<SegmentDisplaySwitcherProps, 'onChange'>
  >(
    (value) => {
      if (value === 'fiveSegs') {
        logEvent('Show 5segs based who and what analysis')
      } else if (value === 'nineSegs') {
        logEvent('Show 9segs based who and what analysis')
      }
    },
    [logEvent],
  )

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isExecuting}>
      <VStack spacing={0} verticalAppearance="stretch-scroll">
        <Box id="top" />

        <VStack spacing={8}>
          <VStack spacing={4}>
            <LabelingContainer label={t('brand_group')}>
              <SharedBrandGroupSelector />
            </LabelingContainer>

            <LabelingContainer label={t('switch_view_6')}>
              <HStack spacing={4}>
                <DataDisplayTypeAtomSwitcher
                  dataDisplayTypeAtom={dataDisplayTypeAtom}
                />

                <SegmentDisplaySwitcher
                  segmentDisplayTypeAtom={segmentDisplayTypeAtom}
                  onChange={handleSegmentDisplayTypeChange}
                />
              </HStack>
            </LabelingContainer>

            <LabelingContainer label={t('select_brand')}>
              <BrandSelectorContainer />
            </LabelingContainer>

            <LabelingContainer
              label={
                <LabelWithTooltip
                  href={MANUAL_URLS.npiDriverQuestionHelp}
                  label={t('who_and_what_npi_driver')}
                  tooltip={t('who_and_what_npi_driver_tooltip')}
                />
              }
            >
              <QuestionSelectionBox
                logEventName="Select NPI driver question"
                questions={npiDriverQuestions}
                value={npiDriverQuestionId}
                onChange={setNpiDriverQuestionId}
              />
            </LabelingContainer>

            <LabelingContainer
              label={
                <LabelWithTooltip
                  href={MANUAL_URLS.importanceQuestionHelp}
                  label={t('who_and_what_importance')}
                  tooltip={t('who_and_what_importance_tooltip')}
                />
              }
            >
              <QuestionSelectionBox
                logEventName="Select importance question"
                questions={importanceQuestions}
                value={importanceQuestionId}
                onChange={setImportanceQuestionId}
              />
            </LabelingContainer>

            <LabelingContainer
              label={
                <LabelWithTooltip
                  href={MANUAL_URLS.liftSaQuestionHelp}
                  label={t('who_and_what_lift_sa')}
                  tooltip={t('who_and_what_lift_sa_tooltip')}
                />
              }
            >
              <QuestionSelectionBox
                logEventName="Select lift SA question"
                questions={liftSaQuestions}
                value={liftSaQuestionId}
                onChange={setLiftSaQuestionId}
              />
            </LabelingContainer>

            <AnalyzeButton className={styles.analyzeButton} />
          </VStack>

          <Outputs />
        </VStack>
      </VStack>
    </PageContainer>
  )
}
